/** Shared with SimpleFooter — platform keys for social link ENUM fields. */
export const SOCIAL_ICON_ENUM_VALUES = [
  'facebook',
  'youtube',
  'twitter',
  'instagram',
  'pinterest',
  'wechat',
  'wecom',
  'weibo',
  'tiktok',
  'linkedin',
];

const SOCIAL_ICON_LABELS = {
  facebook: 'Facebook',
  youtube: 'YouTube',
  twitter: 'Twitter',
  instagram: 'Instagram',
  pinterest: 'Pinterest',
  wechat: 'WeChat',
  wecom: 'WeCom',
  weibo: 'Weibo',
  tiktok: 'TikTok',
  linkedin: 'LinkedIn',
};

/** { label, value } pairs for custom widget propsSchema / page editor dropdowns. */
export const SOCIAL_ICON_ENUM_OPTIONS = SOCIAL_ICON_ENUM_VALUES.map((value) => ({
  label: SOCIAL_ICON_LABELS[value] || value,
  value,
}));

/** ff-header navItems[].key — restored only when type is already ENUM but options were stripped. */
export const NAV_ITEM_ENUM_OPTIONS = [
  { label: 'EAI Robotics (mega menu)', value: 'robotics' },
  { label: 'EAI Automotive (mega menu)', value: 'automotive' },
  { label: 'Discovery (mega menu)', value: 'discovery' },
  { label: 'Education (link)', value: 'education' },
];

/** logo.type — restored only when type is already ENUM but options were stripped. */
export const LOGO_TYPE_ENUM_OPTIONS = [
  { label: 'Logo image', value: 'image' },
  { label: 'Text', value: 'text' },
];

/** True when `options` looks like a select list (SimpleFooter strings or {label,value} pairs). */
export function isEnumOptionsList(options) {
  if (!Array.isArray(options) || options.length === 0) return false;
  return options.every((o) => (
    typeof o === 'string'
    || typeof o === 'number'
    || (o && typeof o === 'object' && ('value' in o || 'label' in o))
  ));
}

/** Options for a schema field, including known fallbacks when nested options were stripped. */
export function resolveEnumOptionsForSchema(item, parentKey) {
  if (item?.options?.length) return item.options;
  if ((parentKey === 'socials' || parentKey === 'socialLinks') && item?.key === 'icon') {
    return SOCIAL_ICON_ENUM_OPTIONS;
  }
  if (parentKey === 'navItems' && item?.key === 'key') return NAV_ITEM_ENUM_OPTIONS;
  if (parentKey === 'logo' && item?.key === 'type') return LOGO_TYPE_ENUM_OPTIONS;
  // Legacy schemas without parent context (icon-only).
  if (!parentKey && item?.key === 'icon') return SOCIAL_ICON_ENUM_OPTIONS;
  return [];
}

/**
 * True when a schema field should render as a select.
 * Matches prior behavior (`type === 'ENUM'`) and also handles custom-widget schemas
 * where nested fields kept `options` but were saved as `TEXT`.
 */
export function isEnumPropSchema(item) {
  if (!item || typeof item !== 'object') return false;
  if (isEnumOptionsList(item.options)) return true;
  if (item.type === 'ENUM') return true;
  return false;
}

/** Keys used by EventHeroImage, AddToCalendar, ff-hero, etc. */
const DATETIME_KEY_RE = /(?:DateTime|dateTime|_date)$/i;

export function isDateTimePropKey(key) {
  return DATETIME_KEY_RE.test(String(key || ''));
}

export function isDateTimePropValue(value) {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return false;
  return Object.prototype.hasOwnProperty.call(value, 'date')
    || Object.prototype.hasOwnProperty.call(value, 'timezone');
}

export function normalizeDateTimeDefaultValue(value) {
  if (isDateTimePropValue(value)) return value;
  return {};
}

/**
 * Repair custom-widget schema fields that were saved as TEXT/OBJECT after JSON import
 * before DATETIME was supported, or inferred from { date, timezone } value shapes.
 */
export function upgradeDateTimePropSchemaItem(item) {
  if (!item || typeof item !== 'object') return item;

  const enriched = { ...item };
  const looksLikeDateTime = isDateTimePropKey(enriched.key)
    || isDateTimePropValue(enriched.defaultValue);
  const shouldBeDateTime = enriched.type === 'DATETIME'
    || enriched.type === 'DATE'
    || ((enriched.type === 'TEXT' || enriched.type === 'OBJECT') && looksLikeDateTime);

  if (!shouldBeDateTime) return enriched;

  enriched.type = 'DATETIME';
  enriched.children = undefined;
  enriched.defaultValue = normalizeDateTimeDefaultValue(enriched.defaultValue);
  return enriched;
}

/** True when a schema field should render with DateTimePicker. */
export function isDateTimePropSchema(item) {
  if (!item || typeof item !== 'object') return false;
  if (item.type === 'DATETIME' || item.type === 'DATE') return true;
  if (item.type === 'TEXT' && isDateTimePropKey(item.key)) return true;
  if (item.type === 'OBJECT' && isDateTimePropKey(item.key) && isDateTimePropValue(item.defaultValue)) return true;
  return false;
}

/**
 * Fill missing ENUM options on props schema items (e.g. after a save that dropped nested options).
 * Does not change field types except when `options` is already a select list (TEXT → ENUM).
 */
export function enrichPropsSchemaItems(items, parentKey) {
  if (!Array.isArray(items)) return items;

  return items.map((item) => {
    if (!item || typeof item !== 'object') return item;

    let enriched = upgradeDateTimePropSchemaItem(item);

    // Custom widgets: nested select fields sometimes keep options but lose ENUM type.
    if (isEnumOptionsList(enriched.options) && enriched.type !== 'ENUM') {
      enriched.type = 'ENUM';
    }

    // Only restore options on fields already declared as ENUM — never force unrelated fields.
    if (enriched.type === 'ENUM' && !enriched.options?.length) {
      const fallbackOptions = resolveEnumOptionsForSchema(enriched, parentKey);
      if (fallbackOptions.length > 0) {
        enriched.options = fallbackOptions;
      }
    }

    // socialLinks[].icon (SimpleFooter) and socials[].icon — legacy normalization.
    if ((parentKey === 'socials' || parentKey === 'socialLinks') && enriched.key === 'icon') {
      if (enriched.type !== 'ENUM') {
        enriched.type = 'ENUM';
      }
      if (!enriched.options || enriched.options.length === 0) {
        enriched.options = SOCIAL_ICON_ENUM_OPTIONS;
      }
    } else if (
      enriched.type === 'ENUM'
      && (!enriched.options || enriched.options.length === 0)
      && enriched.key === 'icon'
      && !parentKey
    ) {
      enriched.options = SOCIAL_ICON_ENUM_OPTIONS;
    }

    if (Array.isArray(enriched.children) && enriched.children.length > 0) {
      enriched.children = enrichPropsSchemaItems(enriched.children, enriched.key);
    }

    return enriched;
  });
}
